import { useTranslation } from 'react-i18next';
import { addDays, addMonths, endOfMonth, startOfMonth, startOfWeek } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate, longMonth, shortDay } from '@utils/i18n';
import { cn } from '@utils/cn';
import { WEEKDAY_ORDER } from '../multiDay/constants';
import { dayKey } from '../multiDay/helpers';
import { MonthGridProps } from './types';

const navClass = 'flex h-9 w-9 items-center justify-center text-neutral-400';

export const MonthGrid = ({ month, onMonthChange, getDay, onTap }: MonthGridProps) => {
  const { t } = useTranslation('app');
  const first = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const last = endOfMonth(month);
  const cells: Date[] = [];
  for (let day = first; day <= last || cells.length % 7; day = addDays(day, 1)) cells.push(day);
  const weekdayNames = WEEKDAY_ORDER.map((weekday) =>
    shortDay(addDays(first, (weekday + 6) % 7)).slice(0, 2),
  );

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label={t('scheduling.daysPicker.previousMonth')}
          onClick={() => onMonthChange(addMonths(month, -1))}
          className={navClass}>
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-[15px] font-medium text-neutral-800">
          {longMonth(month)} {month.getFullYear()}
        </span>
        <button
          type="button"
          aria-label={t('scheduling.daysPicker.nextMonth')}
          onClick={() => onMonthChange(addMonths(month, 1))}
          className={navClass}>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 pb-1 text-center text-xs text-neutral-600">
        {weekdayNames.map((name) => (
          <span key={name}>{name}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((date) => {
          const key = dayKey(date);
          const inMonth = date.getMonth() === month.getMonth();
          const day = inMonth ? getDay(key) : { disabled: true };
          return (
            <button
              key={key}
              type="button"
              disabled={day.disabled}
              aria-pressed={day.selected}
              aria-label={[formatDate(date), day.label].filter(Boolean).join(', ')}
              onClick={() => onTap(key)}
              className="relative flex h-10 items-center justify-center disabled:cursor-default">
              <span
                className={cn(
                  'flex h-[34px] w-[34px] items-center justify-center rounded-full text-sm text-neutral-700',
                  !inMonth && 'text-neutral-300',
                  day.today && 'font-bold text-primary-600',
                  day.muted && 'text-neutral-300',
                  day.struck && 'line-through',
                  day.selected && 'font-bold text-white',
                  day.selected && (day.tone === 'warning' ? 'bg-warning-500' : 'bg-primary-500'),
                )}>
                {date.getDate()}
              </span>
              {day.dot && (
                <span className="absolute bottom-0.5 left-1/2 h-[5px] w-[5px] -translate-x-1/2 rounded-full bg-warning-500" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
